import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Upload, X, Images } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, pimg, errMsg } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { name: "", category_slug: "", price: "", weight: "", description: "", ingredients: "", storage_instructions: "", image: "", available: true, best_seller: false, stock: 0, weight_grams: 0, customizable: false, eggless_available: false };

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(null);
  const [stockDraft, setStockDraft] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/products"), api.get("/categories")]).then(([p, c]) => {
      setProducts(p.data); setCats(c.data); setLoading(false);
      const d = {}; p.data.forEach((x) => (d[x.id] = x.stock ?? 0)); setStockDraft(d);
    });
  };
  useEffect(load, []);

  const saveStock = async (p) => {
    const val = parseInt(stockDraft[p.id]);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid stock number"); return; }
    try {
      await api.put(`/admin/products/${p.id}`, {
        name: p.name, category_slug: p.category_slug, price: p.price, weight: p.weight,
        description: p.description, ingredients: p.ingredients, storage_instructions: p.storage_instructions,
        image: p.image, available: p.available, best_seller: p.best_seller,
        stock: val, weight_grams: p.weight_grams || 0, customizable: !!p.customizable, eggless_available: !!p.eggless_available,
      });
      toast.success(`Stock updated to ${val}`);
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, stock: val } : x)));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openNew = () => { setEditing(null); setForm({ ...empty, category_slug: cats[0]?.slug || "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, price: String(p.price) }); setOpen(true); };

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name || !form.category_slug || !form.price) { toast.error("Name, category and price are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) || 0, weight_grams: parseInt(form.weight_grams) || 0 };
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Product deleted"); load();
  };

  const uploadPhotoFor = async (p, file) => {
    setPhotoBusy(p.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const payload = {
        name: p.name, category_slug: p.category_slug, price: p.price, weight: p.weight,
        description: p.description, ingredients: p.ingredients, storage_instructions: p.storage_instructions,
        image: data.url, available: p.available, best_seller: p.best_seller,
        stock: p.stock, weight_grams: p.weight_grams || 0, customizable: !!p.customizable, eggless_available: !!p.eggless_available,
      };
      await api.put(`/admin/products/${p.id}`, payload);
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, image: data.url } : x)));
      toast.success(`Photo updated for ${p.name}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPhotoBusy(null); }
  };

  return (
    <AdminLayout title="Products">
      <div className="flex justify-between items-center mb-6 gap-3">
        <p className="text-muted-foreground">{products.length} products</p>
        <div className="flex gap-2">
          <button data-testid="photo-manager-btn" onClick={() => setPhotoOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-white border border-[#E5DFD3] text-choco font-semibold px-4 py-3 hover:border-goldsoft transition-colors min-h-[48px]">
            <Images size={18} /> <span className="hidden sm:inline">Photo Manager</span><span className="sm:hidden">Photos</span>
          </button>
          <button data-testid="add-product-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-xl bg-choco text-cream font-semibold px-5 py-3 hover:bg-chocodark transition-colors min-h-[48px]">
            <Plus size={18} /> <span className="hidden sm:inline">Add Product</span><span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 size={36} className="animate-spin text-goldsoft" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {products.map((p) => (
            <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-white rounded-2xl border border-[#E5DFD3] p-4 flex gap-4">
              <img src={pimg(p.image)} alt={p.name} className="h-20 w-20 rounded-xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-choco line-clamp-1">{p.name}</p>
                <p className="text-xs text-muted-foreground">{cats.find((c) => c.slug === p.category_slug)?.name}</p>
                <p className="font-display text-lg font-bold text-choco mt-1">{formatINR(p.price)}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.available ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{p.available ? "Available" : "Hidden"}</span>
                  {(p.stock ?? 0) <= 0 ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800">Out of stock</span>
                    : (p.stock <= 5 ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-800">Only {p.stock} left</span>
                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-800">Stock {p.stock}</span>)}
                  {p.best_seller && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Best seller</span>}
                  {p.customizable && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">Customizable</span>}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-muted-foreground">Stock</span>
                  <input data-testid={`stock-input-${p.id}`} type="number" value={stockDraft[p.id] ?? 0} onChange={(e) => setStockDraft({ ...stockDraft, [p.id]: e.target.value })} className="w-16 h-9 rounded-lg border border-[#E5DFD3] bg-cream px-2 text-sm" />
                  <button data-testid={`stock-save-${p.id}`} onClick={() => saveStock(p)} className="h-9 rounded-lg bg-choco text-cream text-xs font-medium px-3 hover:bg-chocodark">Update</button>
                </div>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="inline-flex items-center gap-1 text-sm text-choco hover:text-goldsoft"><Pencil size={15} /> Edit</button>
                  <button data-testid={`delete-product-${p.id}`} onClick={() => del(p)} className="inline-flex items-center gap-1 text-sm text-destructive hover:opacity-70"><Trash2 size={15} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-choco">{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-choco">Name</label>
              <Input data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-choco">Category</label>
                <Select value={form.category_slug} onValueChange={(v) => setForm({ ...form, category_slug: v })}>
                  <SelectTrigger data-testid="product-form-category" className="mt-1 h-11 bg-white border-[#E5DFD3]"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">{cats.map((c) => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-choco">Price (₹)</label>
                <Input data-testid="product-form-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-choco">Weight / Quantity</label>
              <Input data-testid="product-form-weight" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder="e.g. 600 g" className="mt-1 h-11 bg-white border-[#E5DFD3]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-choco">Stock Quantity</label>
                <Input data-testid="product-form-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" />
              </div>
              <div>
                <label className="text-sm font-medium text-choco">Weight for delivery (grams)</label>
                <Input data-testid="product-form-weightgrams" type="number" value={form.weight_grams} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} placeholder="e.g. 600" className="mt-1 h-11 bg-white border-[#E5DFD3]" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-choco">Description</label>
              <Textarea data-testid="product-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-white border-[#E5DFD3]" />
            </div>
            <div>
              <label className="text-sm font-medium text-choco">Ingredients</label>
              <Textarea value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} className="mt-1 bg-white border-[#E5DFD3]" />
            </div>
            <div>
              <label className="text-sm font-medium text-choco">Storage Instructions</label>
              <Input value={form.storage_instructions} onChange={(e) => setForm({ ...form, storage_instructions: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" />
            </div>
            <div>
              <label className="text-sm font-medium text-choco">Image</label>
              <div className="flex gap-3 items-center mt-1">
                {form.image ? (
                  <div className="relative">
                    <img src={pimg(form.image)} alt="" className="h-16 w-16 rounded-xl object-cover" />
                    <button onClick={() => setForm({ ...form, image: "" })} className="absolute -top-2 -right-2 bg-destructive text-white rounded-full h-5 w-5 flex items-center justify-center"><X size={12} /></button>
                  </div>
                ) : null}
                <label className="inline-flex items-center gap-2 rounded-xl border border-[#E5DFD3] bg-white px-4 py-2.5 text-sm cursor-pointer hover:border-goldsoft">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload
                  <input data-testid="product-form-upload" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
                </label>
              </div>
              <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="or paste image URL" className="mt-2 h-10 bg-white border-[#E5DFD3] text-xs" />
            </div>
            <div className="flex items-center justify-between bg-white rounded-xl border border-[#E5DFD3] px-4 py-3">
              <span className="text-sm font-medium text-choco">Available</span>
              <Switch data-testid="product-form-available" checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} />
            </div>
            <div className="flex items-center justify-between bg-white rounded-xl border border-[#E5DFD3] px-4 py-3">
              <span className="text-sm font-medium text-choco">Best Seller</span>
              <Switch data-testid="product-form-bestseller" checked={form.best_seller} onCheckedChange={(v) => setForm({ ...form, best_seller: v })} />
            </div>
            <div className="flex items-center justify-between bg-white rounded-xl border border-[#E5DFD3] px-4 py-3">
              <span className="text-sm font-medium text-choco">Customizable (Wheat/Ragi +₹100)</span>
              <Switch data-testid="product-form-customizable" checked={form.customizable} onCheckedChange={(v) => setForm({ ...form, customizable: v })} />
            </div>
            <div className="flex items-center justify-between bg-white rounded-xl border border-[#E5DFD3] px-4 py-3">
              <span className="text-sm font-medium text-choco">Eggless option available (+₹150)</span>
              <Switch data-testid="product-form-eggless" checked={form.eggless_available} onCheckedChange={(v) => setForm({ ...form, eggless_available: v })} />
            </div>
            <button data-testid="product-form-save" onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark transition-colors disabled:opacity-60">
              {saving && <Loader2 size={18} className="animate-spin" />} {editing ? "Update Product" : "Add Product"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
        <DialogContent className="bg-cream max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-choco">Photo Manager</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2 mb-2">Upload the real photo for each product. Changes save instantly and appear on the shop right away.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} data-testid={`photo-item-${p.id}`} className="bg-white rounded-xl border border-[#E5DFD3] p-3 text-center">
                <div className="relative">
                  <img src={pimg(p.image)} alt={p.name} className="h-28 w-full rounded-lg object-cover" />
                  {photoBusy === p.id && <div className="absolute inset-0 bg-white/70 rounded-lg flex items-center justify-center"><Loader2 size={22} className="animate-spin text-goldsoft" /></div>}
                </div>
                <p className="text-xs font-medium text-choco mt-2 line-clamp-2 min-h-[2rem]">{p.name}</p>
                <label className="mt-2 inline-flex items-center justify-center gap-1.5 w-full rounded-lg bg-choco text-cream text-xs font-medium px-3 py-2 cursor-pointer hover:bg-chocodark min-h-[40px]">
                  <Upload size={14} /> Upload
                  <input data-testid={`photo-upload-${p.id}`} type="file" accept="image/*" className="hidden" disabled={!!photoBusy} onChange={(e) => e.target.files[0] && uploadPhotoFor(p, e.target.files[0])} />
                </label>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminProducts;
